
import { useAuth } from './useAuth';
import { useStorage } from './useStorage';
import { usePortfolio } from './usePortfolio';
import { usePartners } from './usePartners';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { BUCKETS } from '@/utils/supabase';
import { toast } from '@/hooks/use-toast';

export const useAdmin = () => {
  const { isAdmin, isAdminChecked, login: authLogin, logout: authLogout } = useAuth();
  const { isInitialized, isInitializing, initializeStorage, uploadFile, deleteFile, hasValidSupabaseConfig } = useStorage();
  
  const [initAttempted, setInitAttempted] = useState(false);
  const [configurationChecked, setConfigurationChecked] = useState(false);
  const [portfolioItems, setPortfolioItems] = useState<any[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Use refs to prevent excessive rerenders and manage refresh state
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTimeRef = useRef<number>(0);
  const refreshCooldownRef = useRef<boolean>(false);
  
  // Memoize the initialized state to prevent unnecessary re-renders
  const supabaseInitialized = useMemo(() => isInitialized, [isInitialized]);
  
  // Get portfolio functions with the correct initialization parameter
  const portfolio = usePortfolio(supabaseInitialized);
  
  // Fix: Destructure portfolio with the correct functions
  const { 
    savePortfolioItems, 
    getPortfolioItems, 
    deletePortfolioItem,
    loadPortfolioItems 
  } = portfolio;
  
  const { savePartners, getPartners, deletePartner } = usePartners(supabaseInitialized);
  
  // Add an event listener for login state changes
  useEffect(() => {
    const handleLoginSuccess = () => {
      console.log("Login success event detected, refreshing state...");
      // Reset initialization attempt tracking
      setInitAttempted(false);
      // This will trigger the initialization effect below with a delay to prevent immediate refresh
      setTimeout(() => {
        refreshPortfolioItems();
      }, 1000);
    };
    
    window.addEventListener('admin-login-success', handleLoginSuccess);
    
    return () => {
      window.removeEventListener('admin-login-success', handleLoginSuccess);
    };
  }, []);
  
  // Improved configuration check
  useEffect(() => {
    if (!configurationChecked) {
      const isValid = hasValidSupabaseConfig();
      
      if (!isValid) {
        console.warn("Invalid Supabase configuration detected during initial check");
      }
      
      setConfigurationChecked(true);
    }
  }, [configurationChecked, hasValidSupabaseConfig]);
  
  // Separate the initialization effect from the admin status effect with improved timing
  useEffect(() => {
    // Only attempt initialization once, when admin status is confirmed
    if (isAdmin && !isInitialized && !isInitializing && !initAttempted && hasValidSupabaseConfig() && isAdminChecked) {
      console.log("Admin detected, initializing storage once...");
      initializeStorage(false, true)
        .catch(err => {
          console.warn("Initial storage initialization failed:", err);
        });
      setInitAttempted(true);
    }
  }, [isAdmin, isInitialized, isInitializing, initializeStorage, initAttempted, hasValidSupabaseConfig, isAdminChecked]);

  // Improved portfolio refresh with better throttling
  const refreshPortfolioItems = useCallback(async () => {
    // Prevent refresh if already in progress
    if (isRefreshing || refreshCooldownRef.current) {
      console.log("Portfolio refresh blocked - already in progress or cooling down");
      return portfolioItems;
    }
    
    // Throttle refreshes to prevent excessive API calls
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < 5000) {
      console.log("Portfolio refresh throttled - too frequent");
      return portfolioItems;
    }
    
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    
    lastRefreshTimeRef.current = now;
    refreshCooldownRef.current = true;
    
    try {
      setIsRefreshing(true);
      console.log("Refreshing portfolio items...");
      
      await loadPortfolioItems();
      const items = await getPortfolioItems();
      console.log(`Portfolio items refreshed, got ${items?.length || 0} items`);
      
      setPortfolioItems(items || []);
      return items;
    } catch (error) {
      console.error("Failed to refresh portfolio items:", error);
      return [];
    } finally {
      setIsRefreshing(false);
      
      // Set a cooldown period before allowing another refresh
      refreshTimeoutRef.current = setTimeout(() => {
        refreshCooldownRef.current = false;
        refreshTimeoutRef.current = null;
      }, 5000); // 5 second cooldown
    }
  }, [getPortfolioItems, loadPortfolioItems, isRefreshing, portfolioItems]);

  // Improved load items effect with better conditions
  useEffect(() => {
    // Skip this effect if we're in the cooldown period
    if (refreshCooldownRef.current) {
      return;
    }
    
    // Only run initial load once when admin status is checked
    if (isAdminChecked && !isRefreshing) {
      const loadOnce = async () => {
        // Add debounce to prevent multiple rapid loads
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
        
        refreshTimeoutRef.current = setTimeout(async () => {
          const now = Date.now();
          // Ensure we don't refresh too frequently
          if (now - lastRefreshTimeRef.current > 5000) {
            try {
              // Set cooldown flag to prevent concurrent refreshes
              refreshCooldownRef.current = true;
              
              console.log(`Admin status confirmed. isAdmin: ${isAdmin}, isInitialized: ${isInitialized}`);
              const items = await refreshPortfolioItems();
              if (items) {
                setPortfolioItems(items);
              }
            } finally {
              // Clear cooldown in 5 seconds
              setTimeout(() => {
                refreshCooldownRef.current = false;
              }, 5000);
            }
          }
        }, 1000); // 1 second debounce
      };
      
      loadOnce();
    }
    
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [isAdminChecked, isAdmin, isInitialized, refreshPortfolioItems, isRefreshing]);

  // Improved login handling
  const login = useCallback(async (username?: string, password?: string) => {
    try {
      console.log("useAdmin: Attempting login");
      const result = await authLogin(username, password);
      
      // Reset initialization attempt tracking on login
      setInitAttempted(false);
      
      // Check for valid configuration before attempting initialization
      if (result.success && hasValidSupabaseConfig()) {
        console.log("Login successful, initializing storage");
        try {
          await initializeStorage(true, true);
          
          // Force refresh portfolio items after successful login
          // Add delay to ensure initialization completes
          setTimeout(async () => {
            await refreshPortfolioItems();
          }, 1500);
        } catch (err) {
          console.warn("Storage initialization failed during login, will retry later");
        }
      } else if (!hasValidSupabaseConfig()) {
        console.warn("Invalid Supabase configuration, skipping initialization");
      }
      
      return result;
    } catch (error) {
      console.error("Error in useAdmin login:", error);
      return { success: false, error };
    }
  }, [authLogin, hasValidSupabaseConfig, initializeStorage, refreshPortfolioItems]);

  const logout = useCallback(async () => {
    try {
      console.log("useAdmin: Logging out");
      const result = await authLogout();
      
      // Reset state
      setInitAttempted(false);
      setPortfolioItems([]); // Clear items on logout
      
      // Wait for portfolio items to refresh after logout
      console.log("Refreshing portfolio items after logout");
      await refreshPortfolioItems();
      
      return result;
    } catch (error) {
      console.error("Error in useAdmin logout:", error);
      return { success: false, error };
    }
  }, [authLogout, refreshPortfolioItems]);

  const checkConfiguration = useCallback(() => {
    const isValid = hasValidSupabaseConfig();
    
    if (isValid) {
      toast({
        title: "Konfiguration OK",
        description: "Supabase-konfigurationen ser giltig ut.",
      });
    } else {
      toast({
        title: "Konfigurationsfel",
        description: "Supabase-konfigurationen är ogiltig. Kontrollera miljövariablerna.",
        variant: "destructive"
      });
    }
    
    return isValid;
  }, [hasValidSupabaseConfig]);

  return {
    isAdmin,
    isAdminChecked,
    isInitialized,
    isInitializing,
    initializeStorage,
    login,
    logout,
    uploadFile,
    deleteFile,
    savePortfolioItems,
    getPortfolioItems,
    deletePortfolioItem,
    savePartners,
    getPartners,
    deletePartner,
    refreshPortfolioItems,
    portfolioItems,
    BUCKETS,
    hasValidSupabaseConfig,
    checkConfiguration
  };
};

export { type PortfolioItem } from './usePortfolio';
export { type Partner } from './usePartners';
